import {
  ApolloClient,
  ApolloLink,
  ApolloQueryResult,
  Observable,
  ObservableQuery,
  OperationVariables,
  QueryOptions,
  WatchQueryOptions,
} from '@apollo/client';

const AUTH_EPOCH_OPERATION_KIND = '__authEpochOperationKind';

enum AuthEpochOperationKind {
  Imperative = 'imperative',
  Watched = 'watched',
}

interface RetirableWatchedQuery {
  retire(): void;
}

/**
 * Apollo deduplicates operations before they reach the link chain. Classifying operations at the
 * client boundary lets the isolation link retire watched queries without silently completing an
 * imperative client.query() promise that happened to share the same document and variables.
 */
export class AuthEpochApolloClient<TCacheShape> extends ApolloClient<TCacheShape> {
  public watchQuery<T = any, TVariables = OperationVariables>(
    options: WatchQueryOptions<TVariables, T>
  ): ObservableQuery<T, TVariables> {
    const observableQuery = super.watchQuery({
      ...options,
      context: {
        ...options.context,
        [AUTH_EPOCH_OPERATION_KIND]: AuthEpochOperationKind.Watched,
      },
    });

    const reobserve = observableQuery.reobserve.bind(observableQuery);
    observableQuery.reobserve = (newOptions, newNetworkStatus) =>
      reobserve(
        newOptions?.context === undefined
          ? newOptions
          : {
              ...newOptions,
              context: {
                ...newOptions.context,
                [AUTH_EPOCH_OPERATION_KIND]: AuthEpochOperationKind.Watched,
              },
            },
        newNetworkStatus
      );

    return observableQuery;
  }

  public query<T = any, TVariables = OperationVariables>(
    options: QueryOptions<TVariables, T>
  ): Promise<ApolloQueryResult<T>> {
    return super.query({
      ...options,
      context: {
        ...options.context,
        [AUTH_EPOCH_OPERATION_KIND]: AuthEpochOperationKind.Imperative,
        queryDeduplication: false,
      },
    });
  }
}

export interface AuthEpochQueryIsolation {
  link: ApolloLink;
  retireWatchedQueries(): void;
}

export const resetApolloStateForAuthTransition = async <TCacheShape>(
  client: ApolloClient<TCacheShape>,
  isolation: AuthEpochQueryIsolation
) => {
  isolation.retireWatchedQueries();
  try {
    await client.cache.reset();
  } finally {
    // Start feature-query refreshes under the committed principal without making authentication
    // wait for their network completion. Queries cancelled by their owning feature retain aborted
    // signals in Apollo's observable context and must not be replayed with that stale signal.
    void client
      .refetchQueries({
        include: 'active',
        onQueryUpdated: observableQuery =>
          !observableQuery.options.context?.fetchOptions?.signal?.aborted,
      })
      .catch(() => undefined);
  }
};

/**
 * Creates the outermost Apollo link for authentication-generation isolation. Retirement is
 * deliberately a silent completion: existing ObservableQuery instances stay reusable while the
 * outgoing network execution is removed from Apollo's in-flight deduplication state.
 */
export const createAuthEpochQueryIsolation = (): AuthEpochQueryIsolation => {
  const watchedQueries = new Set<RetirableWatchedQuery>();

  const link = new ApolloLink((operation, forward) => {
    if (operation.getContext()[AUTH_EPOCH_OPERATION_KIND] !== AuthEpochOperationKind.Watched) {
      return forward(operation);
    }

    return new Observable(observer => {
      let acceptsResults = true;
      const downstreamSubscription: { current?: ZenObservable.Subscription } = {};

      const watchedQuery: RetirableWatchedQuery = {
        retire: () => {
          if (!acceptsResults) {
            return;
          }

          acceptsResults = false;
          watchedQueries.delete(watchedQuery);
          downstreamSubscription.current?.unsubscribe();
          observer.complete();
        },
      };

      const finish = (callback: () => void) => {
        if (!acceptsResults) {
          return;
        }

        acceptsResults = false;
        watchedQueries.delete(watchedQuery);
        callback();
      };

      watchedQueries.add(watchedQuery);
      downstreamSubscription.current = forward(operation).subscribe({
        next: result => {
          if (acceptsResults) {
            observer.next(result);
          }
        },
        error: error => finish(() => observer.error(error)),
        complete: () => finish(() => observer.complete()),
      });

      return () => {
        acceptsResults = false;
        watchedQueries.delete(watchedQuery);
        downstreamSubscription.current?.unsubscribe();
      };
    });
  });

  return {
    link,
    retireWatchedQueries: () => {
      Array.from(watchedQueries).forEach(watchedQuery => watchedQuery.retire());
    },
  };
};
